// App.tsx
import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import MarketingLandingPage from './pages/MarketingLandingPage';
import Pricing from './pages/Pricing';
import MarketingPricing from './pages/MarketingPricing';
import ExperiencePage from './pages/ExperiencePage';
import AboutPage from './pages/AboutPage';
import Billing from './pages/Billing';
import SignIn from './pages/SignIn';
import Schedule from './pages/Schedule';
import Dashboard from './pages/Dashboard';
import Summaries from './pages/Summaries';
import FollowUps from './pages/FollowUps';
import Calendar from './pages/Calendar';
import CRM from './pages/CRM';
import MyDoubles from './pages/MyDoubles';
import KnowledgeBase from './pages/KnowledgeBase';
import Profile from './pages/Profile';
import SignOut from './pages/SignOut';
import MycalLanding from './pages/MycalLanding';
import ContioAILanding from './sales/personalized/ContioAI/ContioAILanding';
import BlogIndex from './pages/Blog/BlogIndex';
import BlogPost from './pages/Blog/BlogPost';
import ErrorBoundary from './components/ErrorBoundary';
import DomainDebugPanel from './components/DomainDebugPanel';
import ScrollToTop from './components/ScrollToTop';

import { FollowUpsProvider } from './context/FollowUpsContext';
import { useAuth } from './context/AuthContext';
import { detectDomain, validateDomainConfig } from './utils/domain';

function App() {
  const { session, loading } = useAuth();

  // Robust domain detection
  const domainInfo = detectDomain();
  const validation = validateDomainConfig();

  // Log warnings in development
  if (domainInfo.isDev && validation.warnings.length > 0) {
    console.warn('Domain configuration warnings:', validation.warnings);
  }

  // If on mycal.live domain, show public scheduling interface
  if (domainInfo.isMycal) {
    return (
      <ErrorBoundary context="MyCal Domain">
        <Router>
          <ScrollToTop />
          <Routes>
            {/* Root landing page for mycal.live */}
            <Route path="/" element={<MycalLanding />} />
            {/* Public scheduling pages - no auth required */}
            <Route path="/:name" element={<Schedule />} />
            {/* Catch all for mycal domain */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        <DomainDebugPanel />
      </ErrorBoundary>
    );
  }

  // If on www.getadouble.com or getadouble.com, show marketing website ONLY
  if (domainInfo.isMarketing) {
    return (
      <ErrorBoundary context="Marketing Website">
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<MarketingLandingPage />} />
            <Route path="/pricing" element={<MarketingPricing />} />
            <Route path="/experience" element={<ExperiencePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/secret/double/ContioAI" element={<ContioAILanding />} />
            <Route path="/signin" element={<SignIn onClose={() => { window.location.href = 'https://app.getadouble.com'; }} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        <DomainDebugPanel />
      </ErrorBoundary>
    );
  }

  // If on app.getadouble.com, show the application
  return (
    <ErrorBoundary context="DoubleMe Domain">
      <FollowUpsProvider>
        <Router>
          <ScrollToTop />
          {loading ? (
            <div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
          ) : (
            <Routes>
              {/* Always available routes */}
              <Route path="/signin" element={<SignIn onClose={() => { /* no-op or redirect */ }} />} />

              {/* 2) Public landing */}
              {!session && (
                <>
                  <Route path="/" element={<MarketingLandingPage />} />
                  <Route path="/pricing" element={<MarketingPricing />} />
                  <Route path="/experience" element={<ExperiencePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/secret/double/ContioAI" element={<ContioAILanding />} />
                  <Route path="/old" element={<LandingPage />} />
                  <Route path="/pricing-old" element={<Pricing />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </>
              )}

              {/* 3) Authenticated app */}
              {session && (
                <>
                  <Route path="/secret/double/ContioAI" element={<ContioAILanding />} />
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="summaries" element={<Summaries />} />
                    <Route path="follow-ups" element={<FollowUps />} />
                    <Route path="calendar" element={<Calendar />} />
                    <Route path="crm" element={<CRM />} />
                    <Route path="doubles" element={<MyDoubles />} />
                    <Route path="knowledge-base" element={<KnowledgeBase />} />
                    <Route path="billing" element={<Billing />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="signout" element={<SignOut />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </>
              )}

              {/* 4) Fallback redirect for any other case */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </Router>
      </FollowUpsProvider>
    </ErrorBoundary>
  );
}

export default App;
